import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap,
  Code,
  Layers,
  BarChart2,
  Shield,
  ShieldCheck,
  Router,
  Cable,
  Lock,
  Blocks,
  Mail,
  ExternalLink,
  ChevronDown,
  MessageSquare,
  TrendingUp,
  MapPin,
  Eye,
  ArrowRight
} from "lucide-react";
import SEO from "../components/SEO";
import { Screen } from "../types";
import EventsHighlightsSection from "../components/EventsHighlightsSection";
import { fetchSettings } from "../hooks/useSettings";

const FAQS = [
  {
    category: "geo",
    question: "Where is InnovaTech located, and do you work with clients outside Jigawa State?",
    answer: "InnovaTech is headquartered at No.2 Salmaniyya Plaza, Opposite TCN, Ibrahim Aliyu Bypass, Dutse, Jigawa State. We provide physical on-site engineering (LAN cabling, high-density Wi-Fi, CCTV, server installations) across Jigawa, Kano, Abuja, and Northern Nigeria, while our custom software development, web applications, and IT consulting services are delivered to businesses and organizations nationwide and internationally.",
    tags: ["Dutse Headquarters", "On-Site Northern Nigeria", "Nationwide Software"],
  },
  {
    category: "students",
    question: "Can university and polytechnic students apply for SIWES (Industrial Training) or NYSC at InnovaTech?",
    answer: "Yes, absolutely! We actively welcome SIWES (Industrial Training) undergraduate students and NYSC corps members into our incubation ecosystem. You will gain practical, hands-on experience building real-world web applications, mobile apps, network infrastructure, and cybersecurity defenses under the direct 1-on-1 mentorship of senior engineers.",
    tags: ["SIWES Placement", "NYSC Primary Assignment", "1-on-1 Mentorship"],
    action: { label: "Apply for SIWES / NYSC", link: "/programmes" }
  },
  {
    category: "services",
    question: "I am not a technical person. How does InnovaTech help my business or school get started?",
    answer: "You do not need any technical background. We start with a friendly, plain-language conversation to understand your workflow, daily challenges, and goals. We handle 100% of the design, software development, hardware setup, and data migration, and we train your staff step-by-step in clear, accessible language until everyone is confident.",
    tags: ["Plain English Guidance", "Staff Training", "Turnkey Setup"],
  },
  {
    category: "services",
    question: "How much do your software development and network installation services cost?",
    answer: "We offer transparent, milestone-based pricing tailored to your project scope with zero hidden fees. Standard website and business portal projects typically take 2 to 6 weeks, while office network cabling and CCTV setups take 2 to 7 days. We provide a clear itemized quote and milestone roadmap before any work begins.",
    tags: ["Milestone Billing", "No Hidden Fees", "Fast Turnaround"],
    action: { label: "Request Free Quote", link: "https://wa.me/2348130237608?text=Hello%20InnovaTech,%20I%20would%20like%20to%20request%20a%20project%20quote." }
  },
  {
    category: "geo",
    question: "Do your engineers visit our physical office, clinic, or school campus for setup?",
    answer: "Yes! Our certified field engineers conduct comprehensive physical site surveys across Dutse, Kano, and surrounding regions. We run structured fiber and LAN cables, configure enterprise Wi-Fi routers and access points, mount 24/7 CCTV surveillance cameras, and provide ongoing on-site maintenance.",
    tags: ["Physical Site Survey", "Structured LAN & Wi-Fi", "CCTV Surveillance"],
    action: { label: "Book Site Inspection", link: "https://wa.me/2348130237608?text=Hello%20InnovaTech,%20I%20want%20to%20book%20a%20site%20inspection%20for%20networking/CCTV." }
  },
  {
    category: "services",
    question: "How does InnovaTech keep our organization’s data, student records, and finances safe?",
    answer: "We build all platforms strictly adhering to the Nigeria Data Protection Regulation (NDPR) and international security standards. We implement AES-256 database encryption, automated cloud backups, multi-factor authentication, and regular vulnerability scanning so your data remains strictly confidential and protected against hackers.",
    tags: ["NDPR Compliance", "Encrypted Backups", "Zero Data Leaks"],
  },
  {
    category: "students",
    question: "What technical courses are available at the InnovaTech Digital Institute?",
    answer: "Our institute offers certified, practical courses in Full-Stack Web Development (React & Node.js), Mobile App Development (Flutter), Cybersecurity & Ethical Hacking, Data Analytics (Python & PowerBI), and Network Engineering. Classes are available both on-site at our Dutse campus and online.",
    tags: ["Digital Institute", "Physical & Online", "Industry Certificates"],
    action: { label: "Browse Courses", link: "/courses" }
  },
];

const STATIC_TEAM = [
  {
    name: "Abdullahi Bala Musa",
    role: "Founder & CEO",
    bio: "Abdullahi Bala Musa is a tech strategist in digital transformation and cybersecurity, leading InnovaTech's growth in Nigeria.",
    img: "/teams/ceo.webp",
    color: "bg-blue-50 dark:bg-blue-900/10",
    border: "border-blue-100 dark:border-blue-500/20",
    socials: {
      linkedin:
        "https://www.linkedin.com/in/abdullahi-bala-musa-cc-93a3513b0?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
      x: "https://x.com/abdoullbmk1",
      instagram: "https://instagram.com/abdoullbmk",
      email: "mailto:abalamusa39@gmail.com",
    },
    tags: ["Leadership", "Strategy", "Digital Transformation"],
  },
  {
    name: "Yusuf Lawan Nuhu",
    role: "Chief Operating Officer (COO)",
    bio: "Yusuf specializes in building secure platforms that enhance business performance. His background spans cybersecurity, full-stack development, and data science.",
    img: "/teams/coo.webp",
    color: "bg-purple-50 dark:bg-purple-900/10",
    border: "border-purple-100 dark:border-purple-500/20",
    socials: {
      linkedin: "https://www.linkedin.com/in/yusuf-lawan-nuhu-79054b245",
      x: "https://x.com/cyberwarrior___",
      email: "mailto:ylawan685@gmail.com",
      github: "https://github.com/Yoosuph",
    },
    tags: ["Critical thinking", "Decision making", "Full stack"],
  },
  {
    name: "Munir Isah",
    role: "Head of Development",
    bio: "With strong capabilities in penetration testing, backend development, and DevOps, Munir has a consistent history of building secure, scalable solutions.",
    img: "/teams/Head of Development.webp",
    color: "bg-orange-50 dark:bg-orange-900/10",
    border: "border-orange-100 dark:border-orange-500/20",
    socials: {
      x: "https://x.com/munier_ie",
      email: "mailto:muonier.ie@gmail.com",
      github: "https://github.com/munier-ie",
    },
    tags: ["DevOps", "System Architecture", "Full stack"],
  },
  {
    name: "Abdulmalik Abubakar Umar",
    role: "Business Manager",
    bio: "Abdulmalik is a strategic Business Manager focused on driving operational excellence and fostering high-impact partnerships at InnovaTech.",
    img: "/teams/Business Manager.webp",
    color: "bg-green-50 dark:bg-green-900/10",
    border: "border-green-100 dark:border-green-500/20",
    socials: {
      linkedin: "https://www.linkedin.com/in/abdulmalik-abubakar-a7289a3a2",
      instagram: "https://www.instagram.com/abduuuul00",
    },
    tags: ["Strategic Partnerships", "Market Analytics", "Efficiency"],
  },
  {
    name: "Aliyu Usman Haruna",
    role: "Developer",
    bio: "Aliyu is an innovative Developer specializing in crafting scalable, secure, and high-performance technical solutions.",
    img: "/teams/Developer.webp",
    color: "bg-red-50 dark:bg-red-900/10",
    border: "border-red-100 dark:border-red-500/20",
    socials: {
      linkedin: "https://www.linkedin.com/in/aliyu-usman-16a31b312",
    },
    tags: ["UI/UX", "API Integration", "Frontend"],
  },
  {
    name: "Adam Muhammad Ibrahim",
    role: "Head of Business Development",
    bio: "Adam drives InnovaTech's market expansion and growth through strategic partnerships and entrepreneurial leadership.",
    img: "/teams/adam.webp",
    color: "bg-cyan-50 dark:bg-cyan-900/10",
    border: "border-cyan-100 dark:border-cyan-500/20",
    socials: {
      x: "https://x.com/3rdkhalifaa?s=21",
      instagram:
        "https://www.instagram.com/schlstt?igsh=Z2p6bDM0OHdheWs%3D&utm_source=qr",
    },
    tags: ["Business Strategy", "Market Expansion", "Strategic Partnerships"],
  },
];

const BRAND_LOGOS = [
  { name: "KanSadCo", src: "/brands/kansadco.webp", className: "h-10 md:h-12" },
  { name: "TEDxDutse", src: "/brands/tedxdutse.webp", className: "h-7 md:h-9", url: "https://www.ted.com/tedx/events/57451" },
  { name: "B. Nakowa Modern Guest Lodge", src: "/brands/bnakowa.webp", className: "h-7 md:h-9", url: "https://b-nakowahotels.com.ng" },
  { name: "DataGrid", src: "/brands/quadtiles.webp", className: "h-8 md:h-10", url: "https://datagrid-ng.com" },
  { name: "DTFS", src: "/brands/dtfs.webp", className: "h-7 md:h-9", url: "https://app.dtfsglobal.com" },
];

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [team, setTeam] = useState<any[]>([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(true);

  // FAQ State
  const [faqFilter, setFaqFilter] = useState<"all" | "services" | "students" | "geo">("all");
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const filteredFaqs = faqFilter === "all" ? FAQS : FAQS.filter((f) => f.category === faqFilter);

  const scrollToServices = () => {
    const element = document.getElementById("core-services");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [heroImages, setHeroImages] = useState<string[]>([
    "/pictures/Image 02.webp",
    "/pictures/Image 03.webp"
  ]);

  // Local image for the institute section (avoids external Unsplash request chain)
  const instituteImage = "/pictures/coding-team.webp";

  useEffect(() => {
    fetchSettings().then((data) => {
      if (data.hero_image) {
        try {
          const parsed = JSON.parse(data.hero_image);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setHeroImages(parsed);
          } else if (typeof data.hero_image === "string" && data.hero_image.trim()) {
            setHeroImages([data.hero_image]);
          }
        } catch (e) {
          if (typeof data.hero_image === "string" && data.hero_image.trim()) {
            setHeroImages([data.hero_image]);
          }
        }
      }
    });
  }, []);

  useEffect(() => {
    fetch("/api/team")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const colorPalettes = [
            { color: "bg-blue-50 dark:bg-blue-900/10", border: "border-blue-100 dark:border-blue-500/20" },
            { color: "bg-purple-50 dark:bg-purple-900/10", border: "border-purple-100 dark:border-purple-500/20" },
            { color: "bg-orange-50 dark:bg-orange-900/10", border: "border-orange-100 dark:border-orange-500/20" },
            { color: "bg-green-50 dark:bg-green-900/10", border: "border-green-100 dark:border-green-500/20" },
            { color: "bg-red-50 dark:bg-red-900/10", border: "border-red-100 dark:border-red-500/20" },
            { color: "bg-cyan-50 dark:bg-cyan-900/10", border: "border-cyan-100 dark:border-cyan-500/20" },
          ];
          const mapped = data.map((m: any, idx: number) => {
            const palette = colorPalettes[idx % colorPalettes.length];
            return {
              ...m,
              bio: m.intro || m.bio,
              img: m.image || m.img,
              color: m.color || palette.color,
              border: m.border || palette.border,
            };
          });
          setTeam(mapped);
        }
      })
      .catch((err) => console.warn("Failed to load team from API:", err))
      .finally(() => setIsLoadingTeam(false));
  }, []);

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 15000);
    return () => clearInterval(timer);
  }, [heroImages]);

  // Animation Logic - High Performance Zero-Reflow Scroll Stacking
  useEffect(() => {
    const cards = cardsRef.current.filter(Boolean) as HTMLDivElement[];
    if (cards.length === 0) return;

    const cardOffsets: number[] = [];

    const measure = () => {
      // Avoid layouts on scroll by caching absolute document offsets once
      cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        cardOffsets[index] = rect.top + window.scrollY;
      });
    };

    // Run initial measurement after a short delay to allow rendering/layout to settle
    const settleTimeout = setTimeout(measure, 600);

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (cardOffsets.length === 0) {
            measure();
          }

          const scrollY = window.scrollY;

          cards.forEach((card, index) => {
            // The card visual container is the first child
            const cardInner = card.firstElementChild as HTMLElement;
            if (!cardInner) return;

            // If it's the last card, it doesn't need to scale down
            if (index === cards.length - 1) return;

            const topOffset = 120 + index * 15;
            const nextTopOffset = 120 + (index + 1) * 15;

            const offsetTop = cardOffsets[index];
            const nextOffsetTop = cardOffsets[index + 1];
            if (offsetTop === undefined || nextOffsetTop === undefined) return;

            // Compute viewport boundaries mathematically without triggering DOM layout calculations
            const cardViewportTop = Math.max(topOffset, offsetTop - scrollY);
            const nextCardViewportTop = Math.max(nextTopOffset, nextOffsetTop - scrollY);

            const distance = nextCardViewportTop - cardViewportTop;
            const transitionRange = 300;

            let scale = 1;

            if (distance < transitionRange && distance > 0) {
              const progress = 1 - distance / transitionRange;
              scale = 1 - progress * 0.05; // Scale down to 0.95
            } else if (distance <= 0) {
              scale = 0.95;
            }

            cardInner.style.transform = `scale(${scale})`;
          });

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", measure);
    window.addEventListener("load", measure);

    // Initial check
    handleScroll();

    return () => {
      clearTimeout(settleTimeout);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", measure);
      window.removeEventListener("load", measure);
    };
  }, [team]);

  return (
    <div className="w-full flex flex-col gap-24 mt-16 md:mt-20">
      <div className="w-full max-w-[1120px] mx-auto px-4 flex flex-col gap-24">
        <SEO
          title="InnovaTech | IT Consulting Agency"
          description="InnovaTech is Nigeria's leading IT consultancy, software engineering partner, and tech academy in Dutse, Jigawa State. Hands-on SIWES placement, cloud systems, cybersecurity & CCTV."
          keywords="InnovaTech, best place to apply or do siwes, best tech partner, best tech academy, top IT consulting agency, siwes placement in dutse jigawa, software development nigeria, tech academy jigawa, it consulting nigeria, learn coding dutse, cctv installation jigawa"
          canonical="/"
        >
          {/* LocalBusiness & Organization schema */}
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": ["LocalBusiness", "EducationalOrganization", "ProfessionalService"],
                "name": "InnovaTech Consultancy Limited",
                "alternateName": "InnovaTech",
                "url": "https://www.innovatech-ng.com",
                "logo": "https://www.innovatech-ng.com/logo/logo.png",
                "image": "https://www.innovatech-ng.com/logo/logo.png",
                "telephone": "+234-813-023-7608",
                "priceRange": "₦₦",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "No.2 Salmaniyya Plaza, Opp. TCN, Ibrahim Aliyu Bypass",
                  "addressLocality": "Dutse",
                  "addressRegion": "Jigawa",
                  "postalCode": "720101",
                  "addressCountry": "NG"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 11.6928,
                  "longitude": 9.3480
                },
                "areaServed": [
                  { "@type": "State", "name": "Jigawa State" },
                  { "@type": "State", "name": "Kano State" },
                  { "@type": "City", "name": "Dutse" },
                  { "@type": "Country", "name": "Nigeria" }
                ],
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+234-813-023-7608",
                  "contactType": "customer service"
                },
                "sameAs": [
                  "https://x.com/innovatech_ng",
                  "https://instagram.com/innovatech-ng",
                  "https://linkedin.com/company/innovatech-ng"
                ]
              }
            `}
          </script>
          {/* WebSite SearchAction — enables Google Sitelinks Searchbox */}
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "InnovaTech",
                "url": "https://www.innovatech-ng.com",
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://www.innovatech-ng.com/?s={search_term_string}"
                  },
                  "query-input": "required name=search_term_string"
                }
              }
            `}
          </script>
          {/* SiteNavigationElement schema to enforce Sitelinks structure */}
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "ItemList",
                "itemListElement": [
                  {
                    "@type": "SiteNavigationElement",
                    "position": 1,
                    "name": "Home",
                    "url": "https://www.innovatech-ng.com/"
                  },
                  {
                    "@type": "SiteNavigationElement",
                    "position": 2,
                    "name": "Services",
                    "url": "https://www.innovatech-ng.com/services"
                  },
                  {
                    "@type": "SiteNavigationElement",
                    "position": 3,
                    "name": "Academy",
                    "url": "https://www.innovatech-ng.com/courses"
                  },
                  {
                    "@type": "SiteNavigationElement",
                    "position": 4,
                    "name": "Programmes",
                    "url": "https://www.innovatech-ng.com/programmes"
                  },
                  {
                    "@type": "SiteNavigationElement",
                    "position": 5,
                    "name": "Team",
                    "url": "https://www.innovatech-ng.com/team"
                  },
                  {
                    "@type": "SiteNavigationElement",
                    "position": 6,
                    "name": "Contact Us",
                    "url": "https://www.innovatech-ng.com/contact"
                  },
                  {
                    "@type": "SiteNavigationElement",
                    "position": 7,
                    "name": "About Us",
                    "url": "https://www.innovatech-ng.com/about"
                  }
                ]
              }
            `}
          </script>
          {/* FAQPage schema for homepage */}
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Where is InnovaTech located in Jigawa State?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "InnovaTech is headquartered at No.2 Salmaniyya Plaza, Opposite TCN, Ibrahim Aliyu Bypass, Dutse, Jigawa State, Nigeria."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Can university and polytechnic students do SIWES industrial training at InnovaTech?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes! InnovaTech is the leading destination for SIWES undergraduate internships and NYSC corps members in Northern Nigeria, offering live project software development and network engineering experience."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Why is InnovaTech the best tech partner for businesses and organizations in Nigeria?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "InnovaTech delivers turnkey IT consulting, secure web & mobile app engineering, enterprise networking, and 24/7 CCTV surveillance with proven delivery across 500+ projects in Nigeria."
                    }
                  }
                ]
              }
            `}
          </script>
        </SEO>
      {/* Hero Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="flex flex-col gap-6 items-center text-center lg:items-start lg:text-left lg:max-w-[540px] mx-auto lg:mx-0">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white dark:bg-white/5 border border-border-subtle dark:border-white/10 w-fit shadow-sm">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
            <span className="text-xs font-semibold text-text-sub dark:text-gray-300 uppercase tracking-wider">
              consultancy agency
            </span>
          </div>
          <h1 className="font-tight text-5xl sm:text-6xl font-bold leading-[1.1] tracking-tight text-text-main dark:text-white">
            Leading ICT Solutions <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-600">
              Provider in Nigeria
            </span>
          </h1>
          <p className="text-lg text-text-sub dark:text-gray-400 leading-relaxed max-w-[480px]">
            We build easy-to-use software, install security systems, and run tech classes.
            Experience fast, simple, and secure technology designed to help your business grow.
          </p>
          <div className="flex flex-wrap justify-center lg:justify-start gap-3 pt-2">
            <button
              onClick={() => navigate("/courses")}
              className="flex items-center justify-center gap-2 bg-primary text-white text-base font-semibold px-6 py-3.5 rounded-xl hover:bg-red-600 transition-all shadow-xl shadow-primary/20 active:scale-95 cursor-pointer"
            >
              <GraduationCap className="w-5 h-5 select-none shrink-0" />
              <span>Apply for Innovatech Academy</span>
            </button>
          </div>
          <div className="flex items-center justify-center lg:justify-start gap-4 pt-4 opacity-80">
            <div className="flex -space-x-3">
              <img
                alt="User 1"
                width="32"
                height="32"
                className="w-8 h-8 rounded-full border-2 border-canvas dark:border-surface-dark object-cover"
                src="/pictures/user1.webp"
              />
              <img
                alt="User 2"
                width="32"
                height="32"
                className="w-8 h-8 rounded-full border-2 border-canvas dark:border-surface-dark object-cover"
                src="/pictures/user2.webp"
              />
              <div className="w-8 h-8 rounded-full border-2 border-canvas dark:border-surface-dark bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[10px] font-bold text-text-main dark:text-white">
                +2k
              </div>
            </div>
            <span className="text-sm font-medium text-text-sub dark:text-gray-400">
              Trusted by 2,000+ businesses
            </span>
          </div>
        </div>
        <div className="relative w-full aspect-square lg:aspect-auto lg:h-[600px] rounded-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-border-subtle dark:border-white/10 shadow-soft group flex items-center justify-center p-8">
          <div className="absolute inset-0 flex items-center justify-center">
            {heroImages.map((src, index) => (
              <img
                key={src}
                alt="Abstract structural visualization"
                width="500"
                height="728"
                sizes="(max-width: 640px) 80vw, (max-width: 1024px) 50vw, 410px"
                className={`w-[80%] h-auto max-h-[90%] object-contain transition-opacity duration-1000 ease-in-out absolute ${index === currentHeroImage ? "opacity-100" : "opacity-0"
                  }`}
                src={src}
                fetchPriority={index === 0 ? "high" : "low"}
                loading={index === 0 ? "eager" : "lazy"}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are Section - Landscape & 3-Column Stats Redesign */}
      <section className="flex flex-col gap-12 py-12 reveal">
        <div className="flex flex-col gap-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
              01 · CORE IDENTITY
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-text-main dark:text-white leading-[1.05]">
              We build robust and <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-600">
                secure digital systems.
              </span>
            </h2>
            <p className="text-lg text-text-sub dark:text-gray-400 leading-relaxed max-w-[50ch]">
              At InnovaTech, we create websites, set up security cameras, and teach computer classes.
              We make sure your systems are safe and easy to use, so you never have to worry about the technology.
            </p>
          </div>
        </div>



        {/* Three Column Stack of Double-Bezel Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {/* Card 1: Our Mission */}
          <div className="p-1.5 rounded-[2rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_20px_40px_-15px_rgba(12,27,46,0.03)] transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(12,27,46,0.08)] flex flex-col h-full group">
            <div className="p-8 rounded-[1.625rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 flex flex-col h-full">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-text-sub dark:text-gray-400 mb-6 block">
                PURPOSE
              </span>
              <h3 className="font-bold text-lg text-text-main dark:text-white mb-3 group-hover:text-primary transition-colors duration-500">
                Our Mission
              </h3>
              <p className="text-sm text-text-sub dark:text-gray-400 leading-relaxed">
                To help local businesses grow and help individuals learn useful computer skills that open doors to great job opportunities.
              </p>
            </div>
          </div>

          {/* Card 2: Our Vision */}
          <div className="p-1.5 rounded-[2rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_20px_40px_-15px_rgba(12,27,46,0.03)] transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(12,27,46,0.08)] flex flex-col h-full group">
            <div className="p-8 rounded-[1.625rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 flex flex-col h-full">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-text-sub dark:text-gray-400 mb-6 block">
                DESTINATION
              </span>
              <h3 className="font-bold text-lg text-text-main dark:text-white mb-3 group-hover:text-primary transition-colors duration-500">
                Our Vision
              </h3>
              <p className="text-sm text-text-sub dark:text-gray-400 leading-relaxed">
                To be the most helpful, friendly, and trusted technology partner and training school in Africa.
              </p>
            </div>
          </div>

          {/* Card 3: Our Impact Stat Card */}
          <div className="p-1.5 rounded-[2rem] bg-slate-950/20 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_20px_40px_-15px_rgba(12,27,46,0.03)] transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(12,27,46,0.12)] flex flex-col h-full group">
            <div className="p-8 rounded-[1.625rem] bg-gradient-to-br from-[#122234] to-[#0C1B2E] border border-black/10 dark:border-white/5 flex flex-col h-full text-white justify-between">
              <div>
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/50 mb-6 block">
                  NETWORK
                </span>
                <h3 className="font-bold text-lg mb-3 group-hover:text-primary transition-colors duration-500">
                  Our Impact
                </h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  Helping businesses and students succeed with technology from our main office in Dutse, Jigawa.
                </p>
              </div>
              <div className="flex items-baseline gap-2 mt-6 pt-4 border-t border-white/10">
                <span className="text-4xl font-bold tracking-tight text-white leading-none">50+</span>
                <span className="font-mono text-[10px] tracking-widest text-white/60 uppercase">Innovators</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section id="core-services" className="flex flex-col gap-12 py-12 reveal">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end">
          <div className="flex flex-col gap-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                02 · What We Do
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-text-main dark:text-white leading-[1.05]">
              Core ICT &amp; Technology Services
            </h2>
            <p className="text-text-sub dark:text-gray-400 text-base max-w-xl">
              Full-stack technology solutions delivered from our Dutse hub to businesses, educational institutions, and government agencies across Nigeria.
            </p>
          </div>
          <div className="flex justify-start lg:justify-end gap-3 flex-wrap">
            <a
              href="https://wa.me/2348130237608?text=Hello%20InnovaTech%2C%20I%20would%20like%20to%20inquire%20about%20your%20ICT%20services."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-700 dark:text-emerald-400 font-semibold text-sm border border-emerald-600/20 transition-all"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Chat on WhatsApp</span>
            </a>
            <button
              onClick={() => navigate("/services")}
              className="group inline-flex items-center gap-3 pl-6 pr-2.5 py-2.5 rounded-full bg-primary text-white font-bold text-sm shadow-lg shadow-primary/20 hover:scale-[0.98] transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)]"
            >
              <span>View Full Catalog</span>
              <span className="size-8 rounded-full bg-white/15 flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.16,1.16,0.3,1)] group-hover:translate-x-1 group-hover:scale-105">
                <svg viewBox="0 0 12 12" strokeWidth="1.6" className="w-3.5 h-3.5 stroke-white fill-none">
                  <path d="M2.5 6h7M6 2.5L9.5 6 6 9.5" />
                </svg>
              </span>
            </button>
          </div>
        </div>

        {/* Bento Grid: 5 Core Cards with Clear Human Tone & Micro-CTAs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
          {/* Card 1: Software Engineering */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-4 p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_20px_40px_-15px_rgba(12,27,46,0.03)] flex flex-col h-full">
            <div className="p-8 rounded-[1.85rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 flex flex-col justify-between h-full min-h-[340px] relative overflow-hidden">
              <div className="absolute inset-0 technical-grid-bg opacity-[0.25] pointer-events-none"></div>
              <div className="relative z-10 flex justify-between items-start w-full">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-text-sub dark:text-gray-400">
                  01 · Software Engineering
                </span>
                <Code className="text-primary w-5 h-5 bg-canvas dark:bg-canvas-dark p-2 rounded-xl border border-black/5 dark:border-white/5" />
              </div>
              <div className="relative z-10 mt-6">
                <h3 className="text-2xl font-bold tracking-tight text-text-main dark:text-white mb-2">
                  Custom Web Applications &amp; Enterprise Software
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed max-w-[50ch] mb-4">
                  InnovaTech designs and builds custom web applications, mobile apps, and administrative portals for businesses, educational institutions, and government agencies in Jigawa and across Nigeria.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {["React & TypeScript", "Mobile Apps (Flutter)", "Database Portals", "API Integrations"].map((f) => (
                    <span
                      key={f}
                      className="px-3 py-1 rounded-full bg-canvas dark:bg-canvas-dark border border-black/5 dark:border-white/5 text-[10px] font-mono tracking-wider text-text-sub dark:text-gray-300"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <div className="pt-3 border-t border-black/5 dark:border-white/5 flex items-center justify-between flex-wrap gap-2">
                  <a
                    href="https://wa.me/2348130237608?text=Hello%20InnovaTech,%20I'm%20interested%20in%20building%20a%20custom%20software/web%20app%20for%20my%20organization."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary dark:text-red-400 hover:underline group/link"
                  >
                    <span>Tell us what you want to build</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" />
                  </a>
                  <button
                    onClick={() => navigate("/services")}
                    className="text-xs text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white transition-colors"
                  >
                    Details &rarr;
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: IT Consultancy */}
          <div className="col-span-1 sm:col-span-1 lg:col-span-2 p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_20px_40px_-15px_rgba(12,27,46,0.03)] flex flex-col h-full">
            <div className="p-8 rounded-[1.85rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 flex flex-col justify-between h-full min-h-[340px] relative overflow-hidden">
              <div className="absolute inset-0 technical-grid-bg opacity-[0.1] pointer-events-none"></div>
              <div className="relative z-10 flex justify-between items-start w-full">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-text-sub dark:text-gray-400">
                  02 · IT Consultancy
                </span>
                <Layers className="text-primary w-5 h-5 bg-canvas dark:bg-canvas-dark p-2 rounded-xl border border-black/5 dark:border-white/5" />
              </div>
              <div className="relative z-10 mt-6">
                <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-2">
                  ICT Consultancy &amp; Audits
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed mb-4">
                  We provide strategic IT advisory services, helping organizations audit their digital infrastructure, eliminate waste, and reduce IT costs by up to 35%.
                </p>
                <div className="flex flex-col gap-1.5 mb-6">
                  {["Infrastructure Audits", "Procurement Advisory", "System Modernization"].map((f) => (
                    <span key={f} className="text-xs text-text-sub dark:text-gray-400 flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-primary flex-shrink-0"></span>
                      {f}
                    </span>
                  ))}
                </div>
                <div className="pt-3 border-t border-black/5 dark:border-white/5">
                  <a
                    href="https://wa.me/2348130237608?text=Hello%20InnovaTech,%20I%20would%20like%20to%20get%20honest%20advice%20and%20an%20audit%20for%20our%20tech%20setup."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary dark:text-red-400 hover:underline group/link"
                  >
                    <span>Get honest advice on your tech setup</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Data Analytics Card (Minimalist Micro-UI Concept) */}
          <div className="col-span-1 sm:col-span-1 lg:col-span-3 p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_20px_40px_-15px_rgba(12,27,46,0.03)] flex flex-col h-full">
            <div className="p-8 rounded-[1.85rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 flex flex-col justify-between h-full min-h-[340px] relative overflow-hidden">
              <div className="absolute inset-0 technical-grid-bg opacity-[0.06] pointer-events-none"></div>

              <div className="relative z-10 flex justify-between items-start w-full">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-text-sub dark:text-gray-400">
                  03 · Data Analytics
                </span>
                <BarChart2 className="text-primary w-5 h-5 bg-canvas dark:bg-canvas-dark p-2 rounded-xl border border-black/5 dark:border-white/5" />
              </div>

              <div className="relative z-10 mt-6">
                <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-2">
                  Data Analytics
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed mb-4">
                  We turn scattered operational records, sales transactions, and inventories into clean, decision-ready reports and real-time dashboards.
                </p>

                {/* Minimalist Dribbble-Style Micro-UI Panel */}
                <div className="p-3.5 rounded-2xl bg-canvas/90 dark:bg-white/[0.03] border border-black/5 dark:border-white/10 mb-5 relative overflow-hidden">
                  {/* Floating Stat Pill with Sparkline */}
                  <div className="flex items-center justify-between gap-3 p-2.5 rounded-xl bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/10 shadow-sm mb-2.5">
                    <div className="flex flex-col">
                      <span className="font-mono text-[9px] text-text-sub dark:text-gray-400 uppercase tracking-wider">Monthly Revenue</span>
                      <span className="text-sm font-bold text-text-main dark:text-white tracking-tight">&#8358;2.48M</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-14 h-5 overflow-visible" viewBox="0 0 56 20" fill="none">
                        <path
                          d="M2 15 Q 14 2, 28 11 T 54 3"
                          stroke="#10b981"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded-md border border-emerald-500/20">
                        +24%
                      </span>
                    </div>
                  </div>

                  {/* Clean Mini Breakdown List */}
                  <div className="flex flex-col gap-1.5 px-1">
                    <div className="flex items-center justify-between text-xs py-1">
                      <div className="flex items-center gap-2">
                        <span className="size-4 rounded-md bg-primary/10 text-primary flex items-center justify-center text-[9px] font-bold">
                          S
                        </span>
                        <span className="text-xs font-medium text-text-main dark:text-gray-300">Sales Tracking</span>
                      </div>
                      <span className="font-mono text-[11px] text-text-sub dark:text-gray-400 font-semibold">65%</span>
                    </div>
                    <div className="flex items-center justify-between text-xs py-1 border-t border-black/5 dark:border-white/5">
                      <div className="flex items-center gap-2">
                        <span className="size-4 rounded-md bg-emerald-500/10 text-emerald-500 flex items-center justify-center text-[9px] font-bold">
                          I
                        </span>
                        <span className="text-xs font-medium text-text-main dark:text-gray-300">Inventory Synced</span>
                      </div>
                      <span className="font-mono text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">100%</span>
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-black/5 dark:border-white/5 flex items-center justify-between">
                  <a
                    href="https://wa.me/2348130237608?text=Hello%20InnovaTech,%20I%20want%20to%20see%20how%20to%20track%20my%20daily%20sales%20and%20stock%20on%20my%20phone."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary dark:text-red-400 hover:underline group/link"
                  >
                    <span>See your daily sales on your phone</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Card 4: Cybersecurity */}
          <div className="col-span-1 sm:col-span-1 lg:col-span-3 p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_20px_40px_-15px_rgba(12,27,46,0.03)] flex flex-col h-full">
            <div className="p-8 rounded-[1.85rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 flex flex-col justify-between h-full min-h-[340px] relative overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] dark:opacity-[0.05] pointer-events-none">
                <Shield size={180} strokeWidth={0.5} className="text-text-sub dark:text-white" />
              </div>
              <div className="relative z-10 flex justify-between items-start w-full">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-text-sub dark:text-gray-400">
                  04 · Cybersecurity
                </span>
                <span className="sr-only">Cybersecurity</span>
                <ShieldCheck className="text-primary w-5 h-5 bg-canvas dark:bg-canvas-dark p-2 rounded-xl border border-black/5 dark:border-white/5" />
              </div>
              <div className="relative z-10 mt-6">
                <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-2">
                  Cybersecurity &amp; Data Protection
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed mb-4">
                  Protect your corporate networks, portals, and client databases from malicious threats with thorough vulnerability assessments, staff security training, and NDPR compliance.
                </p>
                <div className="flex flex-col gap-1.5 mb-6">
                  {["NDPR-Compliant Data Protection", "Vulnerability & Penetration Testing", "Staff Phishing Defense Training"].map((f) => (
                    <span key={f} className="text-xs text-text-sub dark:text-gray-400 flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-primary flex-shrink-0"></span>
                      {f}
                    </span>
                  ))}
                </div>
                <div className="pt-3 border-t border-black/5 dark:border-white/5">
                  <a
                    href="https://wa.me/2348130237608?text=Hello%20InnovaTech,%20I%20want%20to%20request%20a%20security%20check%20to%20protect%20our%20office%20data%20and%20accounts."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary dark:text-red-400 hover:underline group/link"
                  >
                    <span>Lock down your office data &amp; accounts</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Card 5: Networking & CCTV */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-6 p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_20px_40px_-15px_rgba(12,27,46,0.03)] flex flex-col h-full">
            <div className="p-8 rounded-[1.85rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 flex flex-col md:flex-row items-center justify-between gap-8 h-full min-h-[320px] relative overflow-hidden">
              <div className="absolute inset-0 technical-grid-bg opacity-[0.1] pointer-events-none"></div>
              <div className="flex-1 flex flex-col justify-between h-full z-10">
                <div>
                  <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-text-sub dark:text-gray-400 block mb-4">
                    05 · Networking &amp; CCTV
                  </span>
                  <h3 className="text-2xl font-bold tracking-tight text-text-main dark:text-white mb-2">
                    Enterprise LAN Cabling, Long-Range Wi-Fi &amp; CCTV
                  </h3>
                  <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed max-w-[54ch] mb-4">
                    Our field engineers deploy structured fiber/LAN cabling, high-density wireless networks, and 24/7 CCTV surveillance for offices, schools, and commercial facilities across Dutse, Kano, and Northern Nigeria.
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 mt-2 mb-6">
                  <span className="text-xs text-text-main dark:text-gray-300 font-medium flex items-center gap-1.5">
                    <Router className="w-[14px] h-[14px] text-primary" />
                    Structured Fiber &amp; LAN Cabling
                  </span>
                  <span className="text-xs text-text-main dark:text-gray-300 font-medium flex items-center gap-1.5">
                    <Cable className="w-[14px] h-[14px] text-primary" />
                    High-Density Campus Wi-Fi
                  </span>
                  <span className="text-xs text-text-main dark:text-gray-300 font-medium flex items-center gap-1.5">
                    <Eye className="w-[14px] h-[14px] text-primary" />
                    24/7 HD CCTV Surveillance
                  </span>
                  <span className="text-xs text-text-main dark:text-gray-300 font-medium flex items-center gap-1.5">
                    <MapPin className="w-[14px] h-[14px] text-primary" />
                    Dutse &amp; Regional On-Site Teams
                  </span>
                </div>
                <div className="pt-3 border-t border-black/5 dark:border-white/5 flex items-center justify-between flex-wrap gap-3">
                  <a
                    href="https://wa.me/2348130237608?text=Hello%20InnovaTech,%20I%20want%20to%20request%20a%20site%20visit%20for%20office%20Wi-Fi,%20cabling,%20or%20CCTV%20installation."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white font-bold text-xs shadow-md shadow-primary/20 hover:scale-[0.98] transition-all"
                  >
                    <span>Have our engineers visit your building</span>
                    <ArrowRight className="w-3 h-3" />
                  </a>
                  <span className="text-xs text-text-sub dark:text-gray-400">
                    📍 Head office: No.2 Salmaniyya Plaza, Dutse, Jigawa
                  </span>
                </div>
              </div>

              <div className="relative shrink-0 flex justify-center w-full md:w-auto z-10">
                <div className="w-28 h-28 border border-border-subtle dark:border-white/10 rounded-full flex items-center justify-center relative bg-canvas dark:bg-canvas-dark shadow-sm">
                  <div className="w-20 h-20 border border-dashed border-border-subtle dark:border-white/10 rounded-full animate-spin-slow"></div>
                  <Router className="w-[32px] h-[32px] text-primary absolute animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education Wing (Institute) Section */}
      <section className="py-12 reveal">
        {/* Grand Double-Bezel capsule container */}
        <div className="p-2 rounded-[2.5rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_24px_48px_-28px_rgba(12,27,46,0.04)]">
          <div className="rounded-[2.1rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-12 p-8 md:p-12 items-center">
            <div className="flex flex-col justify-center gap-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                  03 · Education Wing
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-text-main dark:text-white leading-[1.05]">
                InnovaTech Digital Institute
              </h2>
              <p className="text-text-sub dark:text-gray-400 leading-relaxed text-base">
                Master the skills of tomorrow. From full-stack development to
                network engineering, our institute offers hands-on courses
                designed by industry experts.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
                <div className="flex items-center gap-3">
                  <div className="size-11 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center text-orange-600 dark:text-orange-400 border border-black/5 dark:border-white/5">
                    <Blocks className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-text-main dark:text-white leading-tight">
                      Practical Coding
                    </p>
                    <p className="text-xs text-text-sub dark:text-gray-400">
                      Real-world projects
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="size-11 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 border border-black/5 dark:border-white/5">
                    <GraduationCap className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-text-main dark:text-white leading-tight">
                      Expert Mentors
                    </p>
                    <p className="text-xs text-text-sub dark:text-gray-400">
                      Guided learning path
                    </p>
                  </div>
                </div>
              </div>

              {/* Apple button-in-button Primary CTA */}
              <div className="pt-4">
                <button
                  onClick={() => navigate("/courses")}
                  className="group inline-flex items-center gap-4 pl-6 pr-2.5 py-2.5 rounded-full bg-primary text-white font-bold text-sm shadow-lg shadow-primary/20 hover:scale-[0.98] transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)]"
                >
                  <span>Browse Courses</span>
                  <span className="size-8 rounded-full bg-white/15 flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.16,1.16,0.3,1)] group-hover:translate-x-1 group-hover:scale-105">
                    <svg viewBox="0 0 12 12" strokeWidth="1.6" className="w-3.5 h-3.5 stroke-white fill-none">
                      <path d="M2.5 6h7M6 2.5L9.5 6 6 9.5" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            {/* Right Side Image - Double Bezel with Spring Rotation */}
            <div className="p-1.5 rounded-[2rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft h-full">
              <div className="relative rounded-[1.625rem] overflow-hidden aspect-[4/3] border border-black/5 dark:border-white/5 h-full group">
                <img
                  width="600"
                  height="400"
                  loading="lazy"
                  alt="Students learning"
                  className="w-full h-full object-cover rounded-[1.625rem] transform group-hover:scale-102 group-hover:rotate-0 md:rotate-2 transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] border border-black/5 dark:border-white/5"
                  src={instituteImage}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stacking Team Section */}
      <section className="mb-12">
        <div className="text-center mb-16">
          <span className="text-primary font-bold tracking-widest text-xs uppercase mb-2 block">
            Our People
          </span>
          <h2 className="font-tight text-3xl font-bold text-text-main dark:text-white">
            Meet the Innovators
          </h2>
        </div>

        {/* Card Stack Container */}
        <div className="flex flex-col gap-12 pb-12">
          {isLoadingTeam ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary/20 border-t-primary"></div>
            </div>
          ) : team.length === 0 ? (
            <div className="text-center py-10 text-text-sub dark:text-gray-400">
              No team members have been added yet.
            </div>
          ) : (
            team.map((member, i) => {
            // Calculate a staggered top offset so they stack visibly
            // We start at a comfortable offset from the top of the viewport (e.g. 120px)
            // Each subsequent card stops 15px lower than the previous one
            const topOffset = 120 + i * 15;

            return (
              <div
                key={i}
                ref={(el) => (cardsRef.current[i] = el)}
                className="sticky group cursor-pointer flex justify-center"
                style={{ top: `${topOffset}px` }}
                onClick={() => navigate("/team")}
              >
                {/* Visual Card Content - This element gets the transform applied */}
                <div className="w-full md:w-[80%] bg-surface dark:bg-surface-dark rounded-[26px] border border-border-subtle dark:border-white/10 shadow-lg p-8 md:p-12 min-h-[320px] md:min-h-[336px] flex flex-col md:flex-row items-center gap-8 md:gap-16 transition-transform duration-75 origin-top relative overflow-hidden">
                  {/* Decorative Background for Image */}
                  <div
                    className={`absolute top-0 left-0 w-32 h-full opacity-5 ${member.color} hidden md:block`}
                  ></div>

                  {/* Image */}
                  <div className="relative shrink-0 flex justify-center">
                    <div
                      className={`absolute inset-0 rounded-full blur-3xl opacity-30 ${member.color} scale-150`}
                    ></div>
                    <div
                      className={`relative size-36 md:size-44 rounded-full p-2 bg-surface dark:bg-surface-dark border-2 ${member.border} shadow-xl`}
                    >
                      <img
                        width="200"
                        height="200"
                        loading="lazy"
                        alt={member.name}
                        className="w-full h-full rounded-full object-cover object-top"
                        src={member.img}
                      />
                    </div>
                  </div>

                  {/* Text */}
                  <div className="flex-1 text-center md:text-left flex flex-col justify-center h-full relative z-10">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-3">
                      <div>
                        <h3 className="text-2xl font-bold text-text-main dark:text-white mb-2">
                          {member.name}
                        </h3>
                        <p className="text-primary dark:text-red-400 font-semibold text-sm">
                          {member.role}
                        </p>
                      </div>
                      <div className="flex gap-3 mt-4 md:mt-0 justify-center md:justify-start">
                        {member.socials.x && (
                          <a
                            href={member.socials.x}
                            target="_blank"
                            rel="noreferrer"
                            className="size-10 rounded-full bg-gray-50 dark:bg-white/5 border border-border-subtle dark:border-white/10 flex items-center justify-center text-text-main dark:text-white hover:text-white hover:bg-black transition-all hover:scale-110"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Visit ${member.name}'s X (Twitter) profile`}
                          >
                            {/* X Icon SVG */}
                            <svg
                              viewBox="0 0 24 24"
                              className="w-5 h-5 fill-current"
                              aria-hidden="true"
                            >
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                            </svg>
                          </a>
                        )}
                        {/* Added Instagram Support for Home Screen */}
                        {member.socials?.instagram && (
                          <a
                            href={member.socials.instagram}
                            target="_blank"
                            rel="noreferrer"
                            className="size-10 rounded-full bg-gray-50 dark:bg-white/5 border border-border-subtle dark:border-white/10 flex items-center justify-center text-text-main dark:text-white hover:text-white hover:bg-[#E1306C] transition-all hover:scale-110"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Visit ${member.name}'s Instagram profile`}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="w-5 h-5 fill-current"
                              aria-hidden="true"
                            >
                              <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
                            </svg>
                          </a>
                        )}
                        {member.socials?.linkedin && (
                          <a
                            href={member.socials.linkedin}
                            target="_blank"
                            rel="noreferrer"
                            className="size-10 rounded-full bg-gray-50 dark:bg-white/5 border border-border-subtle dark:border-white/10 flex items-center justify-center text-text-main dark:text-white hover:text-white hover:bg-[#0077b5] transition-all hover:scale-110"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Visit ${member.name}'s LinkedIn profile`}
                          >
                            {/* LinkedIn Icon SVG */}
                            <svg
                              viewBox="0 0 24 24"
                              className="w-5 h-5 fill-current"
                              aria-hidden="true"
                            >
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                            </svg>
                          </a>
                        )}
                        {member.socials?.email && (
                          <a
                            href={member.socials.email}
                            target="_blank"
                            rel="noreferrer"
                            className="size-10 rounded-full bg-gray-50 dark:bg-white/5 border border-border-subtle dark:border-white/10 flex items-center justify-center text-text-main dark:text-white hover:text-white hover:bg-primary transition-all hover:scale-110"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Send an email to ${member.name}`}
                          >
                            <Mail className="w-5 h-5" />
                          </a>
                        )}
                        {member.socials?.github && (
                          <a
                            href={member.socials.github}
                            target="_blank"
                            rel="noreferrer"
                            className="size-10 rounded-full bg-gray-50 dark:bg-white/5 border border-border-subtle dark:border-white/10 flex items-center justify-center text-text-main dark:text-white hover:text-white hover:bg-[#333] transition-all hover:scale-110"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Visit ${member.name}'s GitHub profile`}
                          >
                            {/* GitHub Icon SVG */}
                            <svg
                              viewBox="0 0 24 24"
                              className="w-5 h-5 fill-current"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                                clipRule="evenodd"
                              ></path>
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                    <p className="text-text-sub dark:text-gray-400 leading-relaxed text-sm line-clamp-2">
                      {member.bio}
                    </p>
                    <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-2">
                      {member.tags?.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 rounded-full bg-canvas dark:bg-canvas-dark border border-border-subtle dark:border-white/10 text-xs font-medium text-text-sub dark:text-gray-400"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          }))}
        </div>
      </section>
    </div>

    {/* Institutions Section */}
    <section className="w-full border-y border-border-subtle dark:border-slate-700 bg-white dark:bg-[#0C1B2E] py-10 overflow-hidden">
      <div className="w-full text-center">
        <p className="text-xs md:text-sm font-semibold uppercase tracking-widest text-text-sub dark:text-slate-400 mb-8">
          Our Trusted Partners
        </p>
        <div className="animate-marquee py-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex gap-16 md:gap-24 px-8 md:px-12 items-center">
              {BRAND_LOGOS.map((brand, bIdx) => {
                const content = (
                  <div className="relative group/brand flex flex-col items-center justify-center shrink-0 pb-6">
                    <div className="flex items-center justify-center grayscale opacity-65 group-hover/brand:grayscale-0 group-hover/brand:opacity-100 dark:brightness-0 dark:invert dark:opacity-70 dark:group-hover/brand:opacity-100 dark:group-hover/brand:brightness-100 dark:group-hover/brand:invert-0 transition-all duration-300 transform group-hover/brand:scale-105">
                      <img
                        src={brand.src}
                        alt={brand.name}
                        className={`${brand.className} w-auto object-contain`}
                        loading="lazy"
                      />
                    </div>
                    {brand.url && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 opacity-0 group-hover/brand:opacity-100 transition-all duration-200 cursor-pointer whitespace-nowrap text-[9px] font-bold text-primary dark:text-red-400 bg-white dark:bg-[#162A45] px-2.5 py-0.5 rounded-full shadow-md border border-border-subtle dark:border-white/10 flex items-center gap-1 z-30 hover:scale-105">
                        View Project
                        <ExternalLink className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </div>
                );

                return brand.url ? (
                  <a
                    key={bIdx}
                    href={brand.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer inline-block focus:outline-none"
                    aria-label={`View ${brand.name} project`}
                  >
                    {content}
                  </a>
                ) : (
                  <div key={bIdx} className="inline-block">
                    {content}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>

    <div className="w-full max-w-[1120px] mx-auto px-4 flex flex-col gap-24">
      {/* Events & Highlights Section */}
      <EventsHighlightsSection />

      {/* Interactive FAQ Section / Conversation & Answers Center (Last section before footer) */}
      <section id="faq-section" className="py-12 reveal">
        <div className="p-2 rounded-[2.5rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_24px_48px_-28px_rgba(12,27,46,0.04)]">
          <div className="rounded-[2.1rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 p-8 md:p-12 flex flex-col gap-10">
            
            {/* FAQ Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="flex flex-col gap-3">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                    Questions &amp; Answers
                  </span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-text-main dark:text-white leading-[1.05]">
                  Frequently Asked Questions
                </h2>
                <p className="text-text-sub dark:text-gray-400 text-sm md:text-base max-w-2xl leading-relaxed">
                  Direct, plain-English answers regarding our ICT services, physical on-site coverage across Jigawa and Northern Nigeria, and student internship (SIWES &amp; NYSC) placements.
                </p>
              </div>

              {/* Quick WhatsApp Contact Pill */}
              <a
                href="https://wa.me/2348130237608?text=Hello%20InnovaTech%2C%20I%20have%20a%20question%20about%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 text-xs font-semibold text-text-main dark:text-white hover:border-primary/50 transition-all shrink-0 w-fit"
              >
                <MessageSquare className="w-3.5 h-3.5 text-primary" />
                <span>Ask directly on WhatsApp</span>
              </a>
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-black/5 dark:border-white/5">
              {[
                { id: "all", label: "All Questions" },
                { id: "services", label: "Services & Pricing" },
                { id: "students", label: "SIWES, NYSC & Courses" },
                { id: "geo", label: "Location & On-Site Support" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setFaqFilter(tab.id as any);
                    setOpenFaqIndex(null);
                  }}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${
                    faqFilter === tab.id
                      ? "bg-primary text-white shadow-md shadow-primary/20"
                      : "bg-canvas dark:bg-canvas-dark text-text-sub dark:text-gray-400 border border-black/5 dark:border-white/5 hover:text-text-main dark:hover:text-white"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Accordion List with Smooth CSS Grid Height Transition */}
            <div className="flex flex-col gap-3 sm:gap-4">
              {filteredFaqs.map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div
                    key={idx}
                    className={`p-1 rounded-[1.25rem] sm:rounded-[1.75rem] transition-all duration-300 border ${
                      isOpen
                        ? "bg-primary/5 dark:bg-primary/10 border-primary/30 dark:border-primary/40 shadow-sm"
                        : "bg-white/40 dark:bg-white/5 border-black/5 dark:border-white/5 shadow-soft"
                    }`}
                  >
                    <div className="rounded-[1rem] sm:rounded-[1.5rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                        className="w-full p-4 sm:p-6 text-left flex items-start justify-between gap-3 sm:gap-4 cursor-pointer hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-colors group"
                        aria-expanded={isOpen}
                      >
                        <div className="flex items-start gap-2.5 sm:gap-3.5 flex-1 min-w-0">
                          <span className="font-mono text-xs sm:text-sm font-semibold text-primary dark:text-red-400 shrink-0 mt-0.5 select-none">
                            {String(idx + 1).padStart(2, "0")}.
                          </span>
                          <span className="font-display tracking-tight text-sm sm:text-base font-semibold text-text-main dark:text-white group-hover:text-primary transition-colors leading-snug sm:leading-normal">
                            {faq.question}
                          </span>
                        </div>
                        <div
                          className={`size-7 sm:size-8 rounded-full flex items-center justify-center shrink-0 border transition-all duration-300 mt-0.5 ${
                            isOpen
                              ? "bg-primary text-white border-primary rotate-180 shadow-sm"
                              : "bg-canvas dark:bg-canvas-dark text-text-sub dark:text-gray-400 border-black/5 dark:border-white/5 group-hover:border-primary/40"
                          }`}
                        >
                          <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </div>
                      </button>

                      {/* Smooth Animated Collapsible Content using CSS Grid */}
                      <div
                        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="px-4 pb-4 pt-1 sm:px-6 sm:pb-6 sm:pt-2 text-xs sm:text-sm text-text-sub dark:text-gray-300 font-normal leading-relaxed border-t border-black/[0.04] dark:border-white/5 sm:pl-10 md:pl-11">
                            <p className="mb-3 leading-relaxed">{faq.answer}</p>
                            {(faq.tags || faq.action) && (
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-black/[0.04] dark:border-white/5">
                                {faq.tags && (
                                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                    {faq.tags.map((tag: string, tIdx: number) => (
                                      <span
                                        key={tIdx}
                                        className="px-2.5 py-0.5 rounded-full bg-canvas dark:bg-canvas-dark border border-black/5 dark:border-white/5 text-[10px] font-mono text-text-sub dark:text-gray-400"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {faq.action && (
                                  faq.action.link.startsWith("http") ? (
                                    <a
                                      href={faq.action.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-xs font-bold text-primary dark:text-red-400 hover:underline w-fit"
                                    >
                                      <span>{faq.action.label}</span>
                                      <ArrowRight className="w-3 h-3" />
                                    </a>
                                  ) : (
                                    <button
                                      onClick={() => navigate(faq.action.link)}
                                      className="inline-flex items-center gap-1 text-xs font-bold text-primary dark:text-red-400 hover:underline w-fit cursor-pointer"
                                    >
                                      <span>{faq.action.label}</span>
                                      <ArrowRight className="w-3 h-3" />
                                    </button>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom Consultation Banner */}
            <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex flex-col gap-1">
                <h4 className="text-base font-bold text-text-main dark:text-white">
                  Have a specific technical question or project to discuss?
                </h4>
                <p className="text-xs sm:text-sm text-text-sub dark:text-gray-400">
                  Our Dutse engineering office is open Monday through Saturday. Talk directly with our technical team.
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0 flex-wrap">
                <a
                  href="https://wa.me/2348130237608?text=Hello%20InnovaTech%2C%20I%20would%20like%20to%20discuss%20a%20project%20for%20my%20organization."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-bold text-xs shadow-md shadow-primary/20 hover:scale-[0.98] transition-all"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span>Chat on WhatsApp</span>
                </a>
                <button
                  onClick={() => navigate("/contact")}
                  className="px-4 py-2.5 rounded-full bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 text-xs font-semibold text-text-main dark:text-white hover:border-primary/50 transition-all"
                >
                  Contact Form
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  </div>
);
};

export default HomeScreen;
